import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import apiServices from '../../../../services/apiService';

const existingAttachment = (data) => {
  if (!data) return { url: '', name: '' };
  const nested = (data.attachments || data.Attachments || [])[0];
  return {
    url: data.attachmentUrl || data.AttachmentUrl || nested?.fileUrl || nested?.FileUrl || '',
    name: data.attachmentName || data.AttachmentName || nested?.fileName || nested?.FileName || ''
  };
};

const RuleFormModal = ({ isOpen, onClose, onSubmit, initialData = null }) => {
  const { categories } = useSelector((state) => state.rulesRegulations || { categories: { data: [] } });
  const isCommercial = window.location.pathname.includes('/zentcomm');
  const wingUrl = isCommercial ? '/WingMaster/GetAllWing' : '/Residential/ResWingMaster/GetAllWing';
  const floorUrl = isCommercial ? '/FloorMaster/GetAllFloor' : '/Residential/ResFloorMaster/GetAllFloor';
  
  const [wings, setWings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    wingId: '',
    floorId: '',
    priority: 'Normal',
    effectiveFrom: '',
    effectiveTo: '',
    requiresAcknowledgement: true,
    status: 'Draft',
    attachment: null
  });

  const currentVersion = initialData ? (initialData.currentVersion || initialData.CurrentVersion || 1) : 0;
  const isPublished = initialData ? (initialData.status || initialData.Status) === 'Published' : false;
  const versionsList = initialData?.versions || initialData?.Versions || [];

  useEffect(() => {
    if (isOpen) {
      let matchedCategoryId = initialData?.categoryId || initialData?.CategoryId;
      if (!matchedCategoryId && (initialData?.categoryName || initialData?.CategoryName)) {
        const catName = initialData.categoryName || initialData.CategoryName;
        const foundCat = categories?.data?.find(c => (c.name || c.Name) === catName);
        if (foundCat) matchedCategoryId = foundCat.id || foundCat.Id;
      }

      setFormData({
        title: initialData?.title || initialData?.Title || '',
        description: initialData?.description || initialData?.Description || '',
        categoryId: matchedCategoryId || '',
        wingId: initialData?.wingId || initialData?.WingId || '',
        floorId: initialData?.floorId || initialData?.FloorId || '',
        priority: initialData?.priority || initialData?.Priority || 'Normal',
        effectiveFrom: initialData?.effectiveFrom || initialData?.EffectiveFrom ? (initialData.effectiveFrom || initialData.EffectiveFrom).split('T')[0] : '',
        effectiveTo: initialData?.effectiveTo || initialData?.EffectiveTo ? (initialData.effectiveTo || initialData.EffectiveTo).split('T')[0] : '',
        requiresAcknowledgement: initialData?.requiresAcknowledgement ?? initialData?.RequiresAcknowledgement ?? true,
        status: initialData?.status || initialData?.Status || 'Draft',
        attachment: null,
        existingAttachmentUrl: existingAttachment(initialData).url,
        existingAttachmentName: existingAttachment(initialData).name
      });
      
      setShowVersionHistory(false);
      fetchWings();
    }
  }, [isOpen, initialData, categories]);

  const fetchWings = async () => {
    try {
      const response = await apiServices({ method: "GET", url: wingUrl });
      if (response?.isSuccess || response?.success || response?.IsSuccess) {
        setWings(response.data?.WingMasterData || response.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch wings", error);
    }
  };

  useEffect(() => {
    if (formData.wingId) {
      const fetchFloors = async () => {
        try {
          const response = await apiServices({ method: "GET", url: floorUrl });
          if (response?.isSuccess || response?.success || response?.IsSuccess) {
            const allFloors = response.data?.FloorMasterData || response.data || [];
            setFloors(allFloors.filter(f => (f.wingId || f.WingId)?.toString() === formData.wingId.toString()));
          }
        } catch (error) {
          console.error("Failed to fetch floors", error);
        }
      };
      fetchFloors();
    } else {
      setFloors([]);
    }
  }, [formData.wingId, floorUrl]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else if (name === 'wingId') {
      setFormData(prev => ({ ...prev, wingId: value, floorId: '' }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFormSubmit = (e, publishAfterSave = false) => {
    if (e && e.preventDefault) e.preventDefault();
    onSubmit({
      ...formData,
      publishAfterSave
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 backdrop-blur-sm transition-opacity p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-auto flex flex-col max-h-[90vh] animate-fadeIn">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-800">
              {initialData ? 'Edit Rule' : 'Create New Rule'}
            </h2>
            {initialData && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200">
                v{currentVersion}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="overflow-y-auto p-6 flex-1">
          {/* Version History Collapsible */}
          {versionsList.length > 0 && (
            <div className="mb-5 bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowVersionHistory(!showVersionHistory)}
                className="w-full px-4 py-2.5 flex justify-between items-center text-left text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Version History ({versionsList.length} revision{versionsList.length > 1 ? 's' : ''})
                </span>
                <span className="text-xs text-indigo-600 font-semibold">
                  {showVersionHistory ? 'Hide' : 'View'}
                </span>
              </button>
              {showVersionHistory && (
                <div className="px-4 py-3 border-t border-slate-200 space-y-2 max-h-40 overflow-y-auto bg-white">
                  {versionsList.map((ver, idx) => (
                    <div key={ver.id || ver.Id || idx} className="flex justify-between items-center text-xs py-1 border-b border-gray-100 last:border-0">
                      <div>
                        <span className="font-semibold text-gray-800 mr-2">v{ver.versionNumber || ver.VersionNumber}</span>
                        <span className="text-gray-600">{ver.title || ver.Title}</span>
                      </div>
                      <span className="text-gray-400">
                        {ver.createdOn || ver.CreatedOn ? new Date(ver.createdOn || ver.CreatedOn).toLocaleDateString() : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <form id="ruleForm" onSubmit={(e) => handleFormSubmit(e, false)} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rule Title *</label>
              <input 
                type="text" 
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                required 
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wing (Optional)</label>
                <select 
                  name="wingId"
                  value={formData.wingId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors bg-white"
                >
                  <option value="">Select Wing</option>
                  {wings.map(w => (
                    <option key={w.id || w.Id} value={w.id || w.Id}>{w.wingName || w.WingName}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Floor (Optional)</label>
                <select 
                  name="floorId"
                  value={formData.floorId}
                  onChange={handleChange}
                  disabled={!formData.wingId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors bg-white disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="">Select Floor</option>
                  {floors.map(f => (
                    <option key={f.id || f.Id} value={f.id || f.Id}>{f.floorName || f.FloorName}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select 
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors bg-white"
                  required
                >
                  <option value="">Select a category</option>
                  {categories?.data?.map(cat => (
                    <option key={cat.id || cat.Id} value={cat.id || cat.Id}>{cat.name || cat.Name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select 
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors bg-white"
                >
                  <option value="Normal">Normal</option>
                  <option value="High">High</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effective From *</label>
                <input 
                  type="date" 
                  name="effectiveFrom"
                  value={formData.effectiveFrom}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Effective To (Optional)</label>
                <input 
                  type="date" 
                  name="effectiveTo"
                  value={formData.effectiveTo}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea 
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                required
              ></textarea>
            </div>

            <div className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200">
              <input 
                id="requiresAcknowledgement"
                type="checkbox" 
                name="requiresAcknowledgement"
                checked={formData.requiresAcknowledgement}
                onChange={handleChange}
                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <label htmlFor="requiresAcknowledgement" className="ml-2 block text-sm text-gray-800 font-medium">
                Requires user acknowledgement
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Attachment (PDF/Image)</label>
              <input 
                type="file" 
                name="attachment"
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                accept=".pdf,image/*,.doc,.docx"
              />
              {formData.existingAttachmentUrl && !(formData.attachment instanceof File) && (
                <a
                  href={formData.existingAttachmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-sm text-indigo-600 hover:text-indigo-800"
                >
                  Current file: {formData.existingAttachmentName || 'Open attachment'}
                </a>
              )}
            </div>
          </form>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-wrap justify-end gap-2.5 shrink-0 rounded-b-xl">
          <button 
            type="button" 
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
          >
            Cancel
          </button>

          {!initialData ? (
            <>
              <button 
                type="button"
                onClick={(e) => handleFormSubmit(e, false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
              >
                Save as Draft
              </button>
              <button 
                type="button"
                onClick={(e) => handleFormSubmit(e, true)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
              >
                Save & Publish (v1)
              </button>
            </>
          ) : isPublished ? (
            <>
              <button 
                type="button"
                onClick={(e) => handleFormSubmit(e, false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                title="Saves edits to this rule without bumping the version"
              >
                Save (Keep v{currentVersion})
              </button>
              <button 
                type="button"
                onClick={(e) => handleFormSubmit(e, true)}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
                title="Saves edits, increments version number to v{currentVersion + 1}, resets acknowledgements, and alerts residents"
              >
                Save & Publish New Version (v{currentVersion + 1})
              </button>
            </>
          ) : (
            <>
              <button 
                type="button"
                onClick={(e) => handleFormSubmit(e, false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors"
              >
                Save Draft
              </button>
              <button 
                type="button"
                onClick={(e) => handleFormSubmit(e, true)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
              >
                Save & Publish (v1)
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RuleFormModal;
